import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import s from "./overlay.module.css";

export interface ToastOptions {
  /** A single button on the toast, e.g. Undo. Gives the user longer (5s) to reach it. */
  action?: { label: string; onAction: () => void };
  durationMs?: number;
}

interface ToastApi {
  show: (message: string, options?: ToastOptions) => void;
}

const ToastContext = createContext<ToastApi>({ show: () => {} });

export const useToast = () => useContext(ToastContext);

interface Current { message: string; action?: ToastOptions["action"] }

/** One message at a time, 2.4s (5s with an action), announced politely. Above the tab bar on phones. Mount once near the root. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<Current | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const show = useCallback((message: string, options?: ToastOptions) => {
    setCurrent({ message, action: options?.action });
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCurrent(null), options?.durationMs ?? (options?.action ? 5000 : 2400));
  }, []);
  useEffect(() => () => clearTimeout(timer.current), []);
  const api = useMemo(() => ({ show }), [show]);
  const act = () => {
    const a = current?.action;
    clearTimeout(timer.current);
    setCurrent(null);
    a?.onAction();
  };
  return (
    <ToastContext.Provider value={api}>
      {children}
      <div role="status" aria-live="polite">
        {current ? (
          <div className={s.toast} data-has-action={current.action ? "" : undefined}>
            <span>{current.message}</span>
            {current.action ? <button type="button" className={s.toastAction} onClick={act}>{current.action.label}</button> : null}
          </div>
        ) : null}
      </div>
    </ToastContext.Provider>
  );
}
